import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { HostOffer, ThreadOverview } from "../../../api/threads/threadDecoders";
import { listThreadHostOffers, withdrawHostOffer } from "../../../api/threads/threadHostingApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useLoad } from "../../../shared/loading/useLoad";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import { refusalText } from "../../../ui/feedback/refusalWords";
import StatusMessage from "../../../ui/feedback/StatusMessage";
import { useThreadContext } from "../page/threadPageContext";
import { actionOffer } from "../page/viewerActions";
import { askToTakeOver, askToTakeOverAutomatically } from "./automaticHosting";
import HostingChoiceForm from "./HostingChoiceForm";
import HostOfferPanel from "./HostOfferPanel";
import HostingRow from "./HostingRow";
import { openHostOffers, shownHostOffer, useHostOfferInbox } from "./incomingHostOffers";

/** How often an open request looks for the host's answer. */
const ANSWER_POLL_MILLISECONDS = 10_000;

/**
 * Someone else's Thread (F55; T-9): whose PC it runs on, and taking it over. With the host's
 * offer, its card shows at once, and its Take over accepts it. Without one, Take over asks
 * the host, after a short confirmation that it then runs on your PC and subscription; the
 * host approves with one click. When the host doesn't hand it over, you fork it and run your
 * copy (Q-19: picking up "is a fork"); a saved Thread offers the fork at once, as its host may
 * be away, and asking still, as its host may only have stopped running it. An offer has one
 * confirmation, its card, never twice (OW-09).
 */
export default function TakeOver({ overview, lastEntryNumber }: { overview: ThreadOverview; lastEntryNumber: number }) {
  const { t } = useTranslation();
  const { nameOf, onForkAt, addressedHostOffer } = useThreadContext();
  const offer = openHostOffers(useHostOfferInbox()).find((candidate) => candidate.threadId === overview.threadId && candidate.state === "pending") ?? null;
  const request = useOwnRequest(overview.threadId);
  const [confirming, setConfirming] = useState(false);
  const host = nameOf(overview.host);
  const live = overview.state === "live";
  // "Runs on" says only where; what it runs with is the "Runs with" row's (W-07).
  const where = live ? t("threads.header.runsOn", { place: t("threads.header.theirPc", { host }) }) : t("threads.header.notRunningTheirs", { host });
  const fork = onForkAt === null ? null : (
    <AppButton variant="primary" size="xs" onClick={() => { onForkAt(lastEntryNumber); }}>{t("threads.header.forkAndRun")}</AppButton>
  );
  // Module C's decision (W-22 slice 4): asking needs permission to propose changes, which it names when missing.
  const asking = actionOffer(overview.viewer, "request_hosting");
  const takeOver = (
    <AppButton
      variant={request.ended ? "secondary" : "primary"}
      size="xs"
      unavailable={asking?.refusal == null ? undefined : refusalText(t, asking.refusal)}
      onClick={() => { setConfirming(true); }}
    >
      {t(request.ended ? "threads.header.askAgain" : "threads.header.takeOver")}
    </AppButton>
  );

  // An offer to you shows its card at once; the card's Take over accepts it, the only one shown (OW-09).
  const shownOffer = shownHostOffer(addressedHostOffer, offer?.offerId ?? null);
  if (shownOffer !== null) {
    return (
      <>
        <HostingRow>
          <span>{where}</span>
        </HostingRow>
        {/* From the link, deciding leaves its address; from the inbox, the decided offer leaves it. */}
        <HostOfferPanel offerId={shownOffer} lastEntryNumber={lastEntryNumber} leavesAddress={shownOffer === addressedHostOffer} />
      </>
    );
  }
  if (request.open !== null) {
    return (
      <HostingRow>
        <span>{t("threads.header.asked", { host })}</span>
        <AppButton variant="secondary" size="xs" onClick={() => { request.withdraw(); }}>{t("threads.header.withdraw")}</AppButton>
      </HostingRow>
    );
  }
  return (
    <>
      <HostingRow>
        <span>{request.ended ? t("threads.header.notHandedOver", { host }) : where}</span>
        {request.ended || !live ? fork : null}
        {confirming ? null : takeOver}
      </HostingRow>
      {confirming ? <AskTheHost overview={overview} host={host} onAsked={(asked) => { setConfirming(false); request.asked(asked); }} onCancel={() => { setConfirming(false); }} /> : null}
    </>
  );
}

/** The confirmation before asking: it then runs on your PC and subscription; the hosting form when the connector needs choosing. */
function AskTheHost({ overview, host, onAsked, onCancel }: { overview: ThreadOverview; host: string; onAsked: (request: HostOffer) => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const [choosing, setChoosing] = useState(false);
  const { state, submit } = useSubmission();
  const busy = state.status === "submitting";
  const ask = () => {
    submit(async () => {
      const asked = await askToTakeOverAutomatically(overview);
      if (asked === null) setChoosing(true);
      else onAsked(asked);
    });
  };
  return (
    <StatusMessage tone="info" title={t("threads.takeOver.title", { host })}>
      <div className="space-y-3">
        <p>{t("threads.takeOver.explanation", { host })}</p>
        {state.status === "failed" ? <ErrorNotice error={state.error} /> : null}
        {choosing ? (
          <HostingChoiceForm
            threadId={overview.threadId}
            current={{ route: overview.settings.route, fallbackRoutes: overview.settings.fallback_routes ?? [], permissionProfile: overview.settings.permission_profile, connectorId: null }}
            submitLabel={t("threads.takeOver.ask", { host })}
            submitting={busy}
            onSubmit={(choice) => { submit(async () => { onAsked(await askToTakeOver(overview, choice)); }); }}
            onCancel={onCancel}
          />
        ) : (
          <div className="flex gap-2">
            <AppButton variant="primary" size="sm" disabled={busy} onClick={ask}>{t("threads.takeOver.ask", { host })}</AppButton>
            <AppButton variant="secondary" size="sm" disabled={busy} onClick={onCancel}>{t("common.cancel")}</AppButton>
          </div>
        )}
      </div>
    </StatusMessage>
  );
}

interface OwnRequest {
  /** Your request in this Thread while the host can still answer it; `null` without one. */
  readonly open: HostOffer | null;
  /** A request of yours ended without the hand-over: declined, withdrawn or unanswered. */
  readonly ended: boolean;
  readonly asked: (request: HostOffer) => void;
  readonly withdraw: () => void;
}

/**
 * Your open request for the host role in this Thread, found again after a reload, and
 * followed until it ends, whether asked on this page or before: when the host approves, you
 * host the Thread and this view goes; when it expires, or the host declines, it has ended.
 */
function useOwnRequest(threadId: string): OwnRequest {
  const { currentUserId } = useThreadContext();
  const load = useCallback((signal: AbortSignal) => listThreadHostOffers(threadId, signal), [threadId]);
  const { state: listing, refresh } = useLoad(load);
  // What you asked on this page, and the listing it is not in yet; a newer listing decides.
  const [asked, setAsked] = useState<{ readonly request: HostOffer; readonly before: readonly HostOffer[] | null } | null>(null);
  // The request that ended without the hand-over, as the follow-up below found.
  const [endedId, setEndedId] = useState<string | null>(null);
  const listed = listing.status === "loaded" ? listing.value : null;
  const mine = listed?.find((candidate) => candidate.state === "requested" && candidate.toUser === currentUserId) ?? null;
  const pending = mine ?? (asked !== null && listed === asked.before ? asked.request : null);
  const open = pending !== null && pending.offerId !== endedId ? pending : null;
  useEffect(() => {
    if (open === null) return undefined;
    const followed = open.offerId;
    const expiry = window.setTimeout(() => { setEndedId(followed); }, Math.max(0, Date.parse(open.expiresAt) - Date.now()));
    const answer = window.setInterval(() => {
      if (document.hidden) return;
      listThreadHostOffers(threadId).then((now) => {
        if (!now.some((candidate) => candidate.offerId === followed)) setEndedId(followed);
      }, () => undefined);
    }, ANSWER_POLL_MILLISECONDS);
    return () => {
      window.clearTimeout(expiry);
      window.clearInterval(answer);
    };
  }, [open, threadId]);
  return {
    open,
    ended: endedId !== null && open === null,
    asked: (request) => {
      setAsked({ request, before: listed });
      setEndedId(null);
      refresh();
    },
    withdraw: () => {
      if (open === null) return;
      const followed = open.offerId;
      // Decided meanwhile (approved, declined or expired): the follow-up finds out.
      withdrawHostOffer(open.offerId).then(() => { setEndedId(followed); }, () => undefined);
    },
  };
}
